import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { formatPrice } from "../../lib/utils";

/**
 * Purchases customers chose to pay for at the business. Each stays
 * unusable until the owner confirms the money arrived ("Mark paid"), or
 * drops it if the customer never pays ("Cancel").
 */
export default function PendingPayments() {
  const [items, setItems] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);

  const load = () =>
    api
      .get("/owner/pending-payments")
      .then(({ data }) => setItems(toRows(data)))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const act = async (row, action) => {
    if (action === "cancel" && !window.confirm(`Cancel ${row.title} for ${row.customer}?`)) return;
    setBusyKey(row.key);
    setError(null);
    try {
      if (action === "paid") {
        await api.post(`/owner/pending-payments/${row.type}/${row.id}/paid`);
      } else {
        await api.delete(`/owner/pending-payments/${row.type}/${row.id}`);
      }
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusyKey(null);
    }
  };

  if (!items) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h1 className="font-montserrat text-2xl font-bold text-slate-900">Payments to collect</h1>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">
        Customers chose to pay for these in person. Mark each one paid once you've received the money — it only becomes
        usable after that.
      </p>

      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

      {items.length === 0 ? (
        <p className="text-muted-foreground">Nothing waiting for payment.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((row) => (
            <Card key={row.key} className="p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-slate-900">{row.title}</p>
                <Badge>{row.kind}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {row.customer}
                {row.phone && ` · ${row.phone}`}
              </p>
              <p className="mt-2 text-xl font-bold text-slate-900">{formatPrice(row.amount)}</p>
              <p className="text-xs text-muted-foreground">Requested {new Date(row.date).toLocaleDateString()}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => act(row, "paid")} disabled={busyKey === row.key}>
                  Mark paid
                </Button>
                <Button size="sm" variant="outline" onClick={() => act(row, "cancel")} disabled={busyKey === row.key}>
                  Cancel
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function toRows({ gift_cards = [], packages = [], memberships = [] }) {
  const person = (u) => ({ customer: u?.full_name || u?.email || "Customer", phone: u?.phone });

  return [
    ...gift_cards.map((g) => ({
      key: `gift_card-${g.id}`,
      type: "gift_card",
      id: g.id,
      kind: "Gift card",
      title: `Gift card ${g.code}`,
      amount: g.original_amount,
      date: g.created_at,
      ...person(g.purchaser),
    })),
    ...packages.map((p) => ({
      key: `package-${p.id}`,
      type: "package",
      id: p.id,
      kind: "Package",
      title: p.service_package?.name || "Package",
      amount: p.purchased_price,
      date: p.created_at,
      ...person(p.customer),
    })),
    ...memberships.map((m) => ({
      key: `membership-${m.id}`,
      type: "membership",
      id: m.id,
      kind: m.status === "pending" ? "Membership" : "Renewal",
      title: m.membership?.name || "Membership",
      amount: m.renewal_due ?? m.purchased_price,
      date: m.updated_at,
      ...person(m.customer),
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));
}
